import { useState, useRef } from "react"
import { Box, Button, FormLabel, IconButton, MenuItem, TextField, Typography } from "@mui/material"
import { X, Upload, Loader2 } from "lucide-react"
import { newsletterImagesApi } from "@crm-src/lib/newsletter-images-api"
import { useToast } from "@crm-src/hooks/use-toast"
// Anchor relative stock/upload URLs to the public host so the picker
// thumbnails render on the SPA origin (same helper the email serializer uses).
import { toPreviewImageUrl } from "@crm-src/lib/public-url"

interface NewsImageSelectorProps {
  selectedImages: string[]
  onChange: (images: string[]) => void
}

// Available images for news items - using internal stock images (professional photos)
const NEWS_IMAGES = [
  // Business & Office
  { id: 1, url: '/stock-images/business-meeting.jpg', name: 'Business Meeting' },
  { id: 2, url: '/stock-images/conference-room.jpg', name: 'Conference Room' },
  { id: 3, url: '/stock-images/data-analytics.jpg', name: 'Analytics Dashboard' },
  { id: 4, url: '/stock-images/team-working.jpg', name: 'Team Working' },
  { id: 5, url: '/stock-images/modern-workspace.jpg', name: 'Modern Workspace' },
  { id: 6, url: '/stock-images/modern-office.jpg', name: 'Modern Office' },
  { id: 7, url: '/stock-images/digital-presentation.jpg', name: 'Digital Presentation' },
  { id: 8, url: '/stock-images/virtual-meeting.jpg', name: 'Virtual Meeting' },
  { id: 9, url: '/stock-images/product-launch.jpg', name: 'Product Launch' },
  { id: 10, url: '/stock-images/business-collaboration.jpg', name: 'Business Collaboration' },
  // Aviation & Airports
  { id: 11, url: '/stock-images/airport-terminal.jpg', name: 'Airport Terminal' },
  { id: 12, url: '/stock-images/air-traffic-control.jpg', name: 'Air Traffic Control' },
  { id: 13, url: '/stock-images/airport-runway.jpg', name: 'Airport Runway' },
  // Police & Law Enforcement
  { id: 14, url: '/stock-images/police-vehicles.jpg', name: 'Police Vehicles' },
  { id: 15, url: '/stock-images/police-patrol.jpg', name: 'Police Patrol' },
  // Fire Department & Emergency
  { id: 16, url: '/stock-images/fire-department.jpg', name: 'Fire Department' },
  { id: 17, url: '/stock-images/firefighters-response.jpg', name: 'Firefighters Response' },
  { id: 18, url: '/stock-images/firefighters-action.jpg', name: 'Firefighters in Action' },
  { id: 19, url: '/stock-images/ambulance-emergency.jpg', name: 'Ambulance Emergency' },
  { id: 20, url: '/stock-images/emergency-vehicles.jpg', name: 'Emergency Vehicles' },
  // Healthcare & Hospital
  { id: 21, url: '/stock-images/hospital-corridor.jpg', name: 'Hospital Corridor' },
  { id: 22, url: '/stock-images/hospital-surgery.jpg', name: 'Hospital Surgery' },
  { id: 23, url: '/stock-images/trauma-center.jpg', name: 'Trauma Center' },
  // Military & Defense
  { id: 24, url: '/stock-images/military-formation.jpg', name: 'Military Formation' },
  { id: 25, url: '/stock-images/military-helicopter.jpg', name: 'Military Helicopter' },
  { id: 26, url: '/stock-images/military-parade.jpg', name: 'Military Parade' },
  // Technology & Infrastructure
  { id: 27, url: '/stock-images/server-room.jpg', name: 'Server Room' },
  { id: 28, url: '/stock-images/control-room.jpg', name: 'Control Room' },
  { id: 29, url: '/stock-images/network-infrastructure.jpg', name: 'Network Infrastructure' },
  // Communications
  { id: 30, url: '/stock-images/radio-tower.jpg', name: 'Radio Tower' },
  { id: 31, url: '/stock-images/communication-tower.jpg', name: 'Communication Tower' },
  { id: 32, url: '/stock-images/satellite-dish.jpg', name: 'Satellite Communications' },
  // Education
  { id: 33, url: '/stock-images/school-building.jpg', name: 'School Building' },
  { id: 34, url: '/stock-images/school-campus.jpg', name: 'School Campus' },
  // Maritime & Ports
  { id: 35, url: '/stock-images/port-harbor.jpg', name: 'Port Harbor' },
  { id: 36, url: '/stock-images/shipping-containers.jpg', name: 'Shipping Containers' },
  { id: 37, url: '/stock-images/naval-vessels.jpg', name: 'Naval Vessels' },
  { id: 38, url: '/stock-images/coast-guard-ship.jpg', name: 'Coast Guard Ship' },
  // Energy & Utilities
  { id: 39, url: '/stock-images/solar-energy.jpg', name: 'Solar Energy' },
  { id: 40, url: '/stock-images/wind-energy.jpg', name: 'Wind Energy' },
  // Security & Surveillance
  { id: 41, url: '/stock-images/security-camera.jpg', name: 'Security Camera' },
  { id: 42, url: '/stock-images/surveillance-system.jpg', name: 'Surveillance System' },
  // Transportation
  { id: 43, url: '/stock-images/highway-infrastructure.jpg', name: 'Highway Infrastructure' },
]

export function NewsImageSelector({ selectedImages, onChange }: NewsImageSelectorProps) {
  const [selectedValue, setSelectedValue] = useState<string>("")
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  const handleAddImage = (imageUrl: string) => {
    if (imageUrl && !selectedImages.includes(imageUrl) && selectedImages.length < 10) {
      onChange([...selectedImages, imageUrl])
      setSelectedValue("")
    }
  }

  const handleRemoveImage = (imageUrl: string) => {
    onChange(selectedImages.filter(img => img !== imageUrl))
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file (JPEG, PNG, GIF, or WebP)",
        variant: "destructive"
      })
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Image must be less than 5MB",
        variant: "destructive"
      })
      return
    }

    try {
      setIsUploading(true)
      const uploadedImage = await newsletterImagesApi.upload(file)

      // Add the uploaded image URL to selected images
      const imageUrl = uploadedImage.url
      if (!selectedImages.includes(imageUrl) && selectedImages.length < 10) {
        onChange([...selectedImages, imageUrl])
      }

      toast({
        title: "Image uploaded",
        description: "Your custom image has been uploaded successfully"
      })

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error: any) {
      console.error('Failed to upload image:', error)
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload image",
        variant: "destructive"
      })
    } finally {
      setIsUploading(false)
    }
  }

  const availableImages = NEWS_IMAGES.filter(img => !selectedImages.includes(img.url))

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <FormLabel sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>News Images ({selectedImages.length}/10)</FormLabel>
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
        Add up to 10 images to display alongside this news item
      </Typography>

      {/* Image Selector Dropdown and Upload Button */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          select
          size="small"
          value={selectedValue}
          onChange={(e) => handleAddImage(e.target.value)}
          sx={{ flex: 1 }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (val) => {
              const selected = val as string
              const image = NEWS_IMAGES.find(img => img.url === selected)
              if (!image) {
                return <Box component="span" sx={{ color: "text.disabled" }}>Select a stock image...</Box>
              }
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box component="img" src={toPreviewImageUrl(image.url)} alt={image.name} sx={{ width: 32, height: 32, borderRadius: 1, objectFit: "cover" }} />
                  <span>{image.name}</span>
                </Box>
              )
            },
          }}
        >
          {availableImages.map((image) => (
            <MenuItem key={image.id} value={image.url}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="img" src={toPreviewImageUrl(image.url)} alt={image.name} sx={{ width: 32, height: 32, borderRadius: 1, objectFit: "cover" }} />
                <span>{image.name}</span>
              </Box>
            </MenuItem>
          ))}
        </TextField>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        <Button
          type="button"
          variant="outlined"
          color="inherit"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || selectedImages.length >= 10}
          startIcon={
            isUploading
              ? <Box component="span" sx={{ display: "inline-flex", animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }}><Loader2 size={16} /></Box>
              : <Upload size={16} />
          }
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
      </Box>

      {/* Selected Images Preview */}
      {selectedImages.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormLabel sx={{ fontSize: 14, color: "text.primary" }}>Selected Images:</FormLabel>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1 }}>
            {selectedImages.map((imageUrl, index) => {
              const imageData = NEWS_IMAGES.find(img => img.url === imageUrl)
              return (
                <Box key={index} sx={{ position: "relative", "&:hover [data-remove]": { opacity: 1 } }}>
                  <Box
                    component="img"
                    src={toPreviewImageUrl(imageUrl)}
                    alt={imageData?.name || 'News image'}
                    sx={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 1, border: 2, borderColor: "divider" }}
                  />
                  <IconButton
                    data-remove
                    size="small"
                    onClick={() => handleRemoveImage(imageUrl)}
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      bgcolor: "error.main",
                      color: "error.contrastText",
                      opacity: 0,
                      transition: "opacity .15s",
                      "&:hover": { bgcolor: "error.dark" },
                    }}
                  >
                    <X size={12} />
                  </IconButton>
                  <Typography sx={{ fontSize: 12, textAlign: "center", mt: 0.5, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {imageData?.name}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
      )}
    </Box>
  )
}
